import React, { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  DatePicker,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import {
  DeleteOutlined,
  GiftOutlined,
  PlusOutlined,
  ReloadOutlined,
  SendOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import {
  CreateRewardInput,
  RewardDto,
  RewardWinnerDto,
  useCreateReward,
  useDeleteReward,
  useEmitVouchers,
  useRewards,
  useRewardWinners,
  useUpdateFulfillment,
} from '@shared/api/hooks/useRewards';
import { useLoyaltyPeriods } from '@shared/api/hooks/useLoyaltyPeriods';


const MONTHS_IT = [
  'Gennaio',
  'Febbraio',
  'Marzo',
  'Aprile',
  'Maggio',
  'Giugno',
  'Luglio',
  'Agosto',
  'Settembre',
  'Ottobre',
  'Novembre',
  'Dicembre',
];

interface RewardFormValues {
  nameIt: string;
  nameEn?: string;
  descriptionIt?: string;
  descriptionEn?: string;
  imageUrl?: string;
  mode: 'period' | 'monthly';
  month?: number;
  year?: number;
  periodId?: string;
  minPosition: number;
  maxPosition: number;
}

interface EmitFormValues {
  expiresAt: Dayjs;
}

function getName(name: RewardDto['name']): string {
  if (typeof name === 'string') return name;
  return name?.it ?? name?.en ?? '—';
}

/** Etichetta di un Periodo di raccolta: "Nome (dd/mm/yyyy → dd/mm/yyyy)". */
export function periodLabel(p: { name: string; startsAt: string; endsAt: string }): string {
  return `${p.name} (${dayjs(p.startsAt).format('DD/MM/YYYY')} → ${dayjs(p.endsAt).format('DD/MM/YYYY')})`;
}

// T-135 — helper PURI (testabili) per la fase corrente mostrata nel BO.

interface PeriodLike {
  id: string;
  name: string;
  startsAt: string;
  endsAt: string;
}

/**
 * Il periodo che contiene `now`, finestra semiaperta [startsAt, endsAt) — stessa
 * semantica del backend (shared/utils/loyalty-periods.findCurrentPeriod). null se
 * `now` cade in un buco tra due periodi, o prima del primo / dopo l'ultimo: quel
 * null NON va mostrato come vuoto, ma dichiarato ("nessuna fase in corso") — una
 * volta l'assenza di fase ci fece credere che la classifica fosse rotta.
 */
export function findCurrentLoyaltyPeriod<T extends PeriodLike>(
  periods: T[],
  now: Date = new Date(),
): T | null {
  const t = now.getTime();
  for (const p of periods) {
    const start = new Date(p.startsAt).getTime();
    const end = new Date(p.endsAt).getTime();
    if (t >= start && t < end) return p;
  }
  return null;
}

/** Il primo periodo che inizia DOPO `now` (per il messaggio "la prossima inizia il…"). */
export function findNextLoyaltyPeriod<T extends PeriodLike>(
  periods: T[],
  now: Date = new Date(),
): T | null {
  const t = now.getTime();
  const upcoming = periods
    .filter((p) => new Date(p.startsAt).getTime() > t)
    .sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime());
  return upcoming[0] ?? null;
}

/**
 * Quanto manca alla chiusura, in giorni di CALENDARIO (più intuitivo di ore per
 * chi amministra). Presuppone `now < endsAt` (fase in corso).
 */
export function phaseCountdownLabel(endsAt: string, now: Date = new Date()): string {
  const days = dayjs(endsAt).startOf('day').diff(dayjs(now).startOf('day'), 'day');
  if (days <= 0) return 'chiude oggi';
  if (days === 1) return 'chiude domani';
  return `chiude tra ${days} giorni`;
}

/**
 * T-135 — riquadro "Fase corrente" in cima alla sezione Loyalty: chi amministra
 * vede a colpo d'occhio quale fase è in corso, da quando a quando, e quanto manca
 * alla chiusura, senza aprire "Periodi di raccolta". La fonte (useLoyaltyPeriods →
 * GET /loyalty-periods) è FRESCA, non la classifica cachata (TTL 300s): un
 * periodo modificato dal BO si riflette subito.
 */
export function CurrentPhaseBanner({ periods }: { periods: PeriodLike[] }): React.ReactElement {
  if (periods.length === 0) {
    return (
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Nessun periodo di raccolta configurato"
        description="Aggiungi un periodo nella pagina “Periodi di raccolta” perché parta l’assegnazione punti."
      />
    );
  }

  const current = findCurrentLoyaltyPeriod(periods);
  if (current) {
    return (
      <Alert
        type="success"
        showIcon
        style={{ marginBottom: 12 }}
        message={
          <Space size="small" wrap>
            <Typography.Text strong>Fase corrente: {current.name}</Typography.Text>
            <Tag color="green">{phaseCountdownLabel(current.endsAt)}</Tag>
          </Space>
        }
        description={`${dayjs(current.startsAt).format('DD/MM/YYYY')} → ${dayjs(
          current.endsAt,
        ).format('DD/MM/YYYY')}`}
      />
    );
  }

  // Nessuna fase corrente: buco tra periodi, o prima/dopo tutti. Lo DICHIARIAMO.
  const next = findNextLoyaltyPeriod(periods);
  const description = next
    ? `La prossima (${next.name}) inizia il ${dayjs(next.startsAt).format('DD/MM/YYYY')}.`
    : 'Tutti i periodi di raccolta configurati sono conclusi.';
  return (
    <Alert
      type="warning"
      showIcon
      style={{ marginBottom: 12 }}
      message="Nessuna fase di raccolta in corso in questo momento"
      description={description}
    />
  );
}

export function RewardsPage(): React.ReactElement {
  const [createModal, setCreateModal] = useState(false);
  const [emitReward, setEmitReward] = useState<RewardDto | null>(null);
  const [winnersReward, setWinnersReward] = useState<RewardDto | null>(null);

  const [form] = Form.useForm<RewardFormValues>();
  const [emitForm] = Form.useForm<EmitFormValues>();

  const { data, isLoading, refetch, isFetching } = useRewards();
  const rewards = data ?? [];

  const createMutation = useCreateReward();
  const deleteMutation = useDeleteReward();
  const emitMutation = useEmitVouchers();
  const mode = Form.useWatch('mode', form);

  // T-121: il premio è legato all'IDENTITÀ del Periodo di raccolta (LoyaltyPeriod),
  // non più a un ordinale di fase. Il backend deriva la finestra punti dal periodo
  // scelto, quindi qui basta l'id.
  const { data: loyaltyPeriods = [] } = useLoyaltyPeriods();
  const periodById = new Map(loyaltyPeriods.map((p) => [p.id, p]));

  const openCreate = (): void => {
    form.resetFields();
    const now = dayjs();
    form.setFieldsValue({
      mode: 'period',
      month: now.month() + 1,
      year: now.year(),
      minPosition: 1,
      maxPosition: 1,
    });
    setCreateModal(true);
  };

  const onSubmitCreate = async (): Promise<void> => {
    const v = await form.validateFields();
    try {
      const input: CreateRewardInput = {
        name: { it: v.nameIt, en: v.nameEn || v.nameIt },
        description: v.descriptionIt
          ? { it: v.descriptionIt, en: v.descriptionEn || v.descriptionIt }
          : undefined,
        imageUrl: v.imageUrl || undefined,
        ...(v.mode === 'period'
          ? { periodId: v.periodId }
          : { month: v.month, year: v.year }),
        minPosition: v.minPosition,
        maxPosition: v.maxPosition,
      };
      await createMutation.mutateAsync(input);
      message.success('Reward creato');
      setCreateModal(false);
    } catch (e) {
      const err = e as { response?: { data?: { message?: string | string[] } } };
      const msg = err.response?.data?.message;
      message.error(Array.isArray(msg) ? msg.join('; ') : msg ?? 'Errore');
    }
  };

  const onDelete = async (id: string): Promise<void> => {
    try {
      await deleteMutation.mutateAsync(id);
      message.success('Reward eliminato');
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(err.response?.data?.message ?? 'Errore');
    }
  };

  const onSubmitEmit = async (): Promise<void> => {
    if (!emitReward) return;
    const v = await emitForm.validateFields();
    try {
      const result = await emitMutation.mutateAsync({
        id: emitReward.id,
        expiresAt: v.expiresAt.toISOString(),
      });
      const issued = (result as { issued?: number }).issued;
      message.success(`Voucher emessi: ${issued ?? '?'}`);
      setEmitReward(null);
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(err.response?.data?.message ?? 'Errore');
    }
  };

  return (
    <Card
      title={
        <Space>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Premi mensili
          </Typography.Title>
          <Tag>{rewards.length}</Tag>
        </Space>
      }
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
            Aggiorna
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Nuovo premio
          </Button>
        </Space>
      }
    >
      {/* T-135 — fase corrente + durata a colpo d'occhio (fonte fresca). */}
      <CurrentPhaseBanner periods={loyaltyPeriods} />
      <Table<RewardDto>
        rowKey="id"
        loading={isLoading}
        dataSource={rewards}
        pagination={false}
        columns={[
          { title: 'Nome', render: (_v, r) => getName(r.name) },
          {
            title: 'Periodo',
            width: 180,
            render: (_v, r) => {
              // T-121: premio di periodo → nome del periodo collegato (identità).
              if (r.periodId != null) {
                const p = periodById.get(r.periodId);
                if (p) return periodLabel(p);
                // Periodo cancellato: il premio conserva la sua finestra.
                const range =
                  r.periodStart && r.periodEnd
                    ? ` (${dayjs(r.periodStart).format('DD/MM/YYYY')} → ${dayjs(r.periodEnd).format('DD/MM/YYYY')})`
                    : '';
                return `Periodo rimosso${range}`;
              }
              if (r.month != null && r.year != null) {
                return `${MONTHS_IT[r.month - 1]} ${r.year}`;
              }
              if (r.periodStart && r.periodEnd) {
                // Legacy (premio di fase pre-T-121, non mappato a un periodo).
                return `${dayjs(r.periodStart).format('DD/MM/YYYY')} → ${dayjs(r.periodEnd).format('DD/MM/YYYY')}`;
              }
              return '—';
            },
          },
          {
            title: 'Posizioni',
            width: 130,
            render: (_v, r) => `#${r.minPosition} – #${r.maxPosition}`,
          },
          {
            title: 'Stato',
            dataIndex: 'isActive',
            width: 100,
            render: (v: boolean) =>
              v ? <Tag color="green">Attivo</Tag> : <Tag color="default">Inattivo</Tag>,
          },
          {
            title: '',
            width: 220,
            align: 'right',
            render: (_v, r) => (
              <Space>
                {r.periodId != null || r.phase != null ? (
                  <Button size="small" icon={<TeamOutlined />} onClick={() => setWinnersReward(r)}>
                    Vincitori
                  </Button>
                ) : null}
                <Button
                  size="small"
                  icon={<SendOutlined />}
                  onClick={() => {
                    emitForm.resetFields();
                    emitForm.setFieldsValue({ expiresAt: dayjs().add(90, 'day') });
                    setEmitReward(r);
                  }}
                >
                  Emetti voucher
                </Button>
                <Popconfirm
                  title="Eliminare premio?"
                  okText="Elimina"
                  okButtonProps={{ danger: true }}
                  cancelText="Annulla"
                  onConfirm={() => onDelete(r.id)}
                >
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </Space>
            ),
          },
        ]}
        locale={{
          emptyText: (
            <div style={{ padding: 32, textAlign: 'center' }}>
              <GiftOutlined style={{ fontSize: 32, color: '#bfbfbf' }} />
              <div style={{ marginTop: 8, color: '#8c8c8c' }}>
                Nessun premio. Crea il primo per assegnarlo ai vincitori del mese.
              </div>
            </div>
          ),
        }}
      />

      {/* Create reward */}
      <Modal
        title="Nuovo premio"
        open={createModal}
        onCancel={() => setCreateModal(false)}
        onOk={onSubmitCreate}
        okText="Crea"
        confirmLoading={createMutation.isPending}
        destroyOnHidden
        width={620}
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item name="nameIt" label="Nome (IT)" rules={[{ required: true, max: 255 }]}>
            <Input placeholder="es. 2 Biglietti Tribuna VIP" />
          </Form.Item>
          <Form.Item name="nameEn" label="Nome (EN)">
            <Input placeholder="es. 2 VIP Stand Tickets" />
          </Form.Item>
          <Form.Item name="descriptionIt" label="Descrizione (IT)">
            <Input.TextArea rows={2} maxLength={500} />
          </Form.Item>
          <Form.Item name="descriptionEn" label="Descrizione (EN)">
            <Input.TextArea rows={2} maxLength={500} />
          </Form.Item>
          <Form.Item name="imageUrl" label="URL immagine">
            <Input placeholder="https://..." />
          </Form.Item>
          <Form.Item name="mode" label="Periodo" rules={[{ required: true }]}>
            <Segmented
              options={[
                { label: 'Periodo di raccolta', value: 'period' },
                { label: 'Mensile (legacy)', value: 'monthly' },
              ]}
            />
          </Form.Item>
          {mode === 'monthly' ? (
            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <Form.Item
                name="month"
                label="Mese"
                rules={[{ required: true }]}
                style={{ width: 200 }}
              >
                <InputNumber min={1} max={12} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item
                name="year"
                label="Anno"
                rules={[{ required: true }]}
                style={{ width: 200 }}
              >
                <InputNumber min={2025} max={2100} style={{ width: '100%' }} />
              </Form.Item>
            </Space>
          ) : (
            <Form.Item
              name="periodId"
              label="Periodo di raccolta"
              rules={[{ required: true, message: 'Seleziona un periodo' }]}
              tooltip="La finestra punti del premio è quella del periodo scelto. I periodi si gestiscono in Loyalty → Periodi di raccolta."
              extra={
                loyaltyPeriods.length === 0
                  ? 'Nessun periodo configurato: crealo prima in Loyalty → Periodi di raccolta.'
                  : undefined
              }
            >
              <Select
                placeholder="Scegli il periodo"
                options={loyaltyPeriods.map((p) => ({ label: periodLabel(p), value: p.id }))}
              />
            </Form.Item>
          )}
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Form.Item
              name="minPosition"
              label="Da posizione"
              rules={[{ required: true }]}
              style={{ width: 200 }}
            >
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="maxPosition"
              label="A posizione"
              rules={[{ required: true }]}
              style={{ width: 200 }}
            >
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>
          </Space>
        </Form>
      </Modal>

      {/* Emit vouchers */}
      <Modal
        title={`Emetti voucher: ${emitReward ? getName(emitReward.name) : ''}`}
        open={emitReward !== null}
        onCancel={() => setEmitReward(null)}
        onOk={onSubmitEmit}
        okText="Emetti"
        confirmLoading={emitMutation.isPending}
        destroyOnHidden
      >
        <Typography.Paragraph type="secondary">
          Verranno emessi voucher ai tifosi nelle posizioni #
          {emitReward?.minPosition} – #{emitReward?.maxPosition} della classifica del periodo
          selezionato. Operazione idempotente.
        </Typography.Paragraph>
        <Form form={emitForm} layout="vertical" preserve={false}>
          <Form.Item name="expiresAt" label="Scadenza voucher" rules={[{ required: true }]}>
            <DatePicker
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
              disabledDate={(d) => d.isBefore(dayjs().startOf('day'))}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Gestione vincitori/riserve — regolamento §9 */}
      <Drawer
        title={`Vincitori: ${winnersReward ? getName(winnersReward.name) : ''}`}
        open={winnersReward !== null}
        onClose={() => setWinnersReward(null)}
        width={520}
        destroyOnHidden
      >
        {winnersReward ? <RewardWinnersPanel reward={winnersReward} /> : null}
      </Drawer>
    </Card>
  );
}

function userLabel(u: RewardWinnerDto['user']): string {
  return u.nickname || u.displayName || u.email || u.id;
}

function RewardWinnersPanel({ reward }: { reward: RewardDto }): React.ReactElement {
  const { data, isLoading, isFetching } = useRewardWinners(reward.id);
  const updateMutation = useUpdateFulfillment(reward.id);

  const toggle = async (
    voucherId: string,
    field: 'contacted' | 'unreachable' | 'waiverSigned' | 'delivered',
    checked: boolean,
  ): Promise<void> => {
    try {
      const result = await updateMutation.mutateAsync({ voucherId, input: { [field]: checked } });
      if (result.promotedUserId) {
        message.success('Vincitore irreperibile: prossima riserva promossa e notificata.');
      }
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(err.response?.data?.message ?? 'Errore aggiornamento');
    }
  };

  if (isLoading) return <Typography.Text type="secondary">Caricamento…</Typography.Text>;
  if (!data || data.winners.length === 0) {
    return (
      <Empty
        description="Nessun vincitore ancora emesso. La chiusura automatica avviene entro un'ora dalla fine fase, oppure emetti manualmente con il pulsante 'Emetti voucher'."
      />
    );
  }

  return (
    <Space direction="vertical" size={20} style={{ width: '100%' }}>
      {data.winners.map((w) => (
        <Card
          key={w.voucherId}
          size="small"
          title={userLabel(w.user)}
          extra={<Tag color={w.status === 'EXPIRED' ? 'red' : w.status === 'REDEEMED' ? 'green' : 'blue'}>{w.status}</Tag>}
        >
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Codice: {w.code} {w.user.email ? `· ${w.user.email}` : ''}
          </Typography.Text>
          <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <Checkbox
              checked={!!w.fulfillment?.contactedAt}
              disabled={updateMutation.isPending}
              onChange={(e) => toggle(w.voucherId, 'contacted', e.target.checked)}
            >
              Contattato
            </Checkbox>
            <Checkbox
              checked={!!w.fulfillment?.unreachableAt}
              disabled={updateMutation.isPending}
              onChange={(e) => toggle(w.voucherId, 'unreachable', e.target.checked)}
            >
              Irreperibile (2gg, 1 chiamata/giorno + email) — promuove la prossima riserva
            </Checkbox>
            <Checkbox
              checked={!!w.fulfillment?.waiverSignedAt}
              disabled={updateMutation.isPending}
              onChange={(e) => toggle(w.voucherId, 'waiverSigned', e.target.checked)}
            >
              Liberatoria firmata
            </Checkbox>
            <Checkbox
              checked={!!w.fulfillment?.deliveredAt}
              disabled={updateMutation.isPending}
              onChange={(e) => toggle(w.voucherId, 'delivered', e.target.checked)}
            >
              Consegnato
            </Checkbox>
          </div>
        </Card>
      ))}

      {data.reserves.length > 0 ? (
        <div>
          <Typography.Title level={5}>Riserve (non pubblicate)</Typography.Title>
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            {data.reserves.map((r) => (
              <div
                key={r.position}
                style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}
              >
                <span>#{r.position} · {userLabel(r.user)}</span>
                {r.promotedAt ? <Tag color="orange">Promossa</Tag> : <Tag>In attesa</Tag>}
              </div>
            ))}
          </Space>
        </div>
      ) : null}
      {isFetching ? <Typography.Text type="secondary" style={{ fontSize: 11 }}>Aggiornamento…</Typography.Text> : null}
    </Space>
  );
}
